import React from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Form,
  Button,
  Modal,
  Icon,
  Select,
  Message,
  Input,
  Checkbox,
} from 'semantic-ui-react';

import {
  get, isEmpty, toNumber,
} from 'lodash';
import Validator from 'validatorjs';
import {
  dateTimeFormattedWithOutTimeZone,
  utcMoment,
} from '../../../utilities/dateUtils';
import client from '../../../apollo';
import apmsLocationListQuery from '../../../graphql/apmsLocationListQuery';
import ExceptionForm, {
  handleInput, initExceptionEndTimeString, initIsNoEndDate, onFormatInput,
} from '../Form';

function initSelectedLocation(action, auctionId) {
  if (action === 'Create') {
    return null;
  }
  return auctionId;
}

function mapAuctionLocationOptions(locations) {
  if (!isEmpty(locations)) {
    return locations.map(location => {
      return {
        value: location.id,
        text: location.name,
      };
    });
  }
  return [];
}

class AuctionExceptionForm extends ExceptionForm {
  maxLengthReason = 30;

  constructor(props) {
    super(props);
    this.state = {
      id: '',
      auctionName: '',
      gracePeriod: '',
      exceptionReason: '',
      exceptionEndTime: '',
      exceptionEndTimeString: '',
      exceptionEndTimeTemp: '',
      createdBy: '',
      createdAt: '',
      updatedBy: '',
      updatedAt: '',
      selectedLocationOption: null,
      isNoEndDate: false,
      action: 'Create',
      error: null,
      errors: {},
      debounceTimeout: null,
      auctionLocationOptions: [],
    };
  }

  componentDidMount() {
    const { auctionException, locations } = this.props;
    const {
      id,
      exceptionSourceId,
      exceptionSourceName,
      gracePeriod,
      exceptionReason,
      exceptionEndTime,
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
    } = auctionException;
    const action = id === 0 ? 'Create' : 'Update';
    const auctionLocationOpts = mapAuctionLocationOptions(locations);
    this.setState({
      id: toNumber(id),
      auctionId: exceptionSourceId,
      auctionName: exceptionSourceName,
      gracePeriod,
      exceptionReason,
      exceptionEndTime,
      exceptionEndTimeTemp: exceptionEndTime,
      exceptionEndTimeString: initExceptionEndTimeString(exceptionEndTime),
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
      isNoEndDate: initIsNoEndDate(id, exceptionEndTime),
      auctionLocationOptions: auctionLocationOpts,
      auctionLocationOptionsCache: auctionLocationOpts,
      action,
      selectedLocationOption: initSelectedLocation(
        action,
        exceptionSourceId
      ),
    });
  }

  locationSearchQuery = keyword => {
    if (!isEmpty(keyword)) {
      client
        .query({
          query: apmsLocationListQuery,
          variables: {
            name: keyword,
            page: 1,
            size: 200,
            active: true,
          },
        })
        .then(({ data }) => {
          const locations = get(data, 'apmsLocationList');
          if (locations) {
            this.setState({
              auctionLocationOptions: mapAuctionLocationOptions(locations),
              isLoading: false,
            });
          } else {
            this.setState({
              auctionLocationOptions: [],
              isLoading: false,
            });
          }
        })
        .catch(() => {
          this.setState({ auctionLocationOptions: [], isLoading: false });
        });
    } else {
      // reset to the location from the load first
      this.setState({
        auctionLocationOptions: mapAuctionLocationOptions(
          this.props.locations
        ),
        isLoading: false,
      });
    }
  };

  handleSelectLocationChange = (event, { value }) => {
    const auction = this.state.auctionLocationOptions.find(
      location => location.value === value
    );
    if (auction) {
      this.setState({
        selectedLocationOption: auction.value, // id
        auctionName: auction.text, // name
      });
    } else {
      this.setState({
        selectedLocationOption: null,
        auctionName: null,
      });
    }
  };

  handleSearchChange = (e, { searchQuery }) => {
    // Cancel the previous timer (if any)
    if (this.state.debounceTimeout) {
      clearTimeout(this.state.debounceTimeout);
    }

    // Set a new timer to delay the API call by 1 second
    const debounceTimeout = setTimeout(() => {
      this.locationSearchQuery(this.state.searchQuery);
    }, 1000);

    this.setState({
      selectedLocationOption: undefined,
      auctionName: null,
      searchQuery,
      isLoading: true,
      debounceTimeout,
    });
  };

  onClickSelect = () => {
    const { auctionLocationOptions, auctionLocationOptionsCache } = this.state;
    if (isEmpty(auctionLocationOptions)) {
      this.setState({ auctionLocationOptions: auctionLocationOptionsCache });
    }
  };

  submitForm = () => {
    this.setState({
      error: null,
      errors: {},
    });
    const { onSubmitHandler } = this.props;
    const {
      id,
      auctionId,
      selectedLocationOption,
      gracePeriod,
      exceptionReason,
      exceptionEndTime,
      exceptionEndTimeString,
      action,
    } = this.state;

    const validationRules = {
      selectedLocationOption: 'required|numeric',
      gracePeriod: 'required|integer|min: 0|max: 60',
    };

    const customerErrorMessage = {
      'required.selectedLocationOption':
        'The Auction Location field is required',
      'numeric.selectedLocationOption':
        'The Auction Location field is required',
      'required.gracePeriod': 'The Grace Period field is required.',
      'min.gracePeriod': 'Grace Period must be greater than or equal to zero.',
      'max.gracePeriod': 'Grace Period must be less than or equal to 60.',
    };

    const validation = new Validator(
      this.state,
      validationRules,
      customerErrorMessage
    );

    const messErrorDate = this.validateEndDateDate(exceptionEndTimeString);
    if (!isEmpty(messErrorDate) || validation.fails()) {
      const newErrors = {
        ...validation.errors.errors,
        exceptionEndTime: messErrorDate,
      };
      this.setState({ errors: newErrors });
      return false;
    }
    onSubmitHandler({
      id: parseInt(id, 10),
      auctionId: action === 'Create' ? selectedLocationOption : auctionId,
      gracePeriod: gracePeriod || '',
      exceptionReason: exceptionReason || '',
      exceptionEndTime: isEmpty(exceptionEndTime)
        ? ''
        : dateTimeFormattedWithOutTimeZone(utcMoment(exceptionEndTime)),
      action,
    });
    return true;
  };

  render() {
    const {
      isModalOpen, onCloseHandler, error, submitting,
    } = this.props;

    const {
      action,
      auctionName,
      selectedLocationOption,
      exceptionEndTime,
      gracePeriod,
      exceptionReason,
      isNoEndDate,
      isLoading,
      errors,
      auctionLocationOptions,
    } = this.state;
    const exceptionEndTimeConverted = isEmpty(exceptionEndTime)
      ? null
      : utcMoment(exceptionEndTime);
    const countRestOfReason = exceptionReason.length;
    const datePickerClassName = isNoEndDate === true ? 'input-disabled' : '';
    const selectAuctionClassName = action === 'Update'
      ? 'selection-override input-disabled'
      : 'selection-override';
    const isDisableSelectAuction = action === 'Update';

    return (
      <Modal
        open={isModalOpen}
        closeIcon
        onClose={onCloseHandler}
        closeOnEscape={false}
        closeOnRootNodeClick={false}
      >
        <Modal.Header>
          {action === 'Create' ? action : 'Edit'} Auction Location Exception
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {error && (
              <Message negative>
                <p>{error}</p>
              </Message>
            )}
            <Form>
              <Form.Group widths='equal'>
                <Form.Field
                  required
                  error={!isEmpty(errors.selectedLocationOption)}
                >
                  <label htmlFor='selectedLocationOption'>Auction Location</label>
                  {action === 'Update' && (
                    <Select
                      id='selectedLocationOption'
                      disabled
                      className={selectAuctionClassName}
                      text={auctionName}
                    />
                  )}
                  {action === 'Create' && (
                    <Select
                      id='selectedLocationOption'
                      search
                      loading={isLoading}
                      disabled={isDisableSelectAuction}
                      className={selectAuctionClassName}
                      options={auctionLocationOptions}
                      value={selectedLocationOption}
                      onInput={handleInput(100)}
                      onChange={this.handleSelectLocationChange}
                      onSearchChange={this.handleSearchChange}
                      onClick={this.onClickSelect}
                      noResultsMessage={
                        auctionLocationOptions
                        && auctionLocationOptions.length === 0
                          ? 'No results found.'
                          : null
                      }
                    />
                  )}
                  {errors.selectedLocationOption && (
                    <span style={{ color: 'red' }}>
                      {errors.selectedLocationOption}
                    </span>
                  )}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field required error={!isEmpty(errors.gracePeriod)}>
                  <label htmlFor='gracePeriod'>Grace Period</label>
                  <Input
                    id='gracePeriod'
                    name='gracePeriod'
                    value={gracePeriod}
                    onChange={this.onChangeHandler('gracePeriod')}
                    onKeyDown={onFormatInput}
                    type='number'
                    min={0}
                    max={60}
                  />
                  {errors.gracePeriod && (
                    <span style={{ color: 'red' }}>
                      {errors.gracePeriod
                      && Array.isArray(errors.gracePeriod)
                      && errors.gracePeriod.length > 1
                        ? errors.gracePeriod[errors.gracePeriod.length - 1]
                        : errors.gracePeriod}
                    </span>
                  )}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field required error={!isEmpty(errors.exceptionEndTime)}>
                  <label htmlFor='exceptionEndTime'>Exception End Date</label>
                  <Checkbox
                    onClick={this.toggleEndDate}
                    label='No end date'
                    checked={isNoEndDate}
                  />

                  <DatePicker
                    id='exceptionEndTime'
                    className={datePickerClassName}
                    style={{ padding: '0', background: 'red' }}
                    selected={exceptionEndTimeConverted}
                    onChange={this.onDateChangeHandler('exceptionEndTime')}
                    onBlur={this.onDateFocusHandler('exceptionEndTimeString')}
                    dateFormat='YYYY-MM-DD'
                    disabled={isNoEndDate}
                    value={isNoEndDate ? '' : null}
                    minDate={this.today}
                  />
                  {errors.exceptionEndTime && (
                    <span style={{ color: 'red' }}>
                      {errors.exceptionEndTime}
                    </span>
                  )}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='exceptionReason'>Exception Reason</label>
                  <Input
                    id='exceptionReason'
                    name='exceptionReason'
                    value={exceptionReason}
                    onChange={this.onChangeHandler('exceptionReason')}
                    onInput={handleInput(30)}
                  />
                  <span style={{ color: 'gray' }}>
                    {countRestOfReason}/{this.maxLengthReason}
                  </span>
                </Form.Field>
              </Form.Group>
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          {submitting && (
            <Button loading positive>
              Submitting...
            </Button>
          )}
          {!submitting && (
            <Button color='green' onClick={this.submitForm} inverted>
              <Icon name='checkmark' /> {action}
            </Button>
          )}
          <Button onClick={onCloseHandler}>Cancel</Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

AuctionExceptionForm.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  error: PropTypes.string,
  submitting: PropTypes.bool,
  auctionException: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exceptionSourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exceptionSourceName: PropTypes.string,
    gracePeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exceptionReason: PropTypes.string,
    exceptionEndTime: PropTypes.string,
    createdBy: PropTypes.string,
    createdAt: PropTypes.string,
    updatedBy: PropTypes.string,
    updatedAt: PropTypes.string,
  }).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })).isRequired,
};

AuctionExceptionForm.defaultProps = {
  error: null,
  submitting: false,
};

export default AuctionExceptionForm;
